import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Bell,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Info,
  Gift
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

const TYPE_ICONS = {
  info: Info,
  warning: AlertCircle,
  promo: Gift
};

const TYPE_COLORS = {
  info: "text-blue-500",
  warning: "text-orange-500",
  promo: "text-accent"
};

export default function AdminAnnouncements() {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    message: "",
    type: "info"
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/admin/announcements`);
      setAnnouncements(response.data.announcements);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/admin");
      }
      toast.error("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newAnnouncement.title || !newAnnouncement.message) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/admin/announcements`, newAnnouncement);
      toast.success("Announcement created!");
      setNewAnnouncement({ title: "", message: "", type: "info" });
      fetchAnnouncements();
    } catch (error) {
      toast.error("Failed to create announcement");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (announcementId) => {
    try {
      await axios.put(`${API}/admin/announcements/${announcementId}/toggle`);
      toast.success("Announcement updated");
      fetchAnnouncements();
    } catch (error) {
      toast.error("Failed to toggle announcement");
    }
  };

  const handleDelete = async (announcementId) => {
    try {
      await axios.delete(`${API}/admin/announcements/${announcementId}`);
      toast.success("Announcement deleted");
      fetchAnnouncements();
    } catch (error) {
      toast.error("Failed to delete announcement");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/admin/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Dashboard
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">ANNOUNCEMENTS</span>
            </div>
            <div className="w-24" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Create Announcement */}
        <Card className="bg-card border-border mb-8">
          <CardHeader>
            <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Create Announcement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="uppercase text-sm tracking-wider">Title</Label>
                <Input
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Announcement title"
                  className="bg-input border-transparent focus:border-primary rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="uppercase text-sm tracking-wider">Type</Label>
                <Select
                  value={newAnnouncement.type}
                  onValueChange={(value) => setNewAnnouncement(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger className="bg-input border-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="promo">Promo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="uppercase text-sm tracking-wider">Message</Label>
              <Textarea
                value={newAnnouncement.message}
                onChange={(e) => setNewAnnouncement(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Write your announcement message..."
                className="bg-input border-transparent focus:border-primary rounded-sm min-h-[100px]"
              />
            </div>
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Announcement"}
            </Button>
          </CardContent>
        </Card>

        {/* Announcements List */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              All Announcements ({announcements.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {announcements.length > 0 ? (
              <div className="space-y-4">
                {announcements.map((announcement, i) => {
                  const Icon = TYPE_ICONS[announcement.type] || Info;
                  const colorClass = TYPE_COLORS[announcement.type] || "text-blue-500";
                  
                  return (
                    <motion.div
                      key={announcement.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={`p-4 rounded-sm border ${announcement.active ? 'border-primary/30 bg-primary/5' : 'border-border bg-secondary/30 opacity-60'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <Icon className={`h-5 w-5 mt-0.5 ${colorClass}`} />
                          <div>
                            <div className="font-bold flex items-center gap-2">
                              {announcement.title}
                              {announcement.active && (
                                <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs uppercase rounded-sm">
                                  Active
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{announcement.message}</p>
                            <div className="text-xs text-muted-foreground mt-2">
                              Created: {new Date(announcement.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggle(announcement.id)}
                            className={announcement.active ? 'text-primary' : 'text-muted-foreground'}
                          >
                            {announcement.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(announcement.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No announcements yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
